<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import AiAgentBrainsAPI from 'dashboard/api/aiAgents/brains';
import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Model editor (D-protocol P1a): surfaces the brain's existing `models` object
// on its own tab. Every key in models is REQUIRED by the schema, so we hydrate
// the whole object into local state, edit in place, and fold it back via the
// same draft-spread save path the other tabs use — never dropping a key and
// preserving every other brain layer. Optimistic locking like the brain
// editor: a 409 means someone else edited; offer reload, never overwrite.

// Datalist suggestions — free entry stays allowed; these just speed up trying
// different Anthropic models.
const MODEL_SUGGESTIONS = [
  'claude-opus-4-8',
  'claude-sonnet-4-6',
  'claude-haiku-4-5-20251001',
  'claude-sonnet-4-5-20250929',
];

const { t } = useI18n();
const store = useStore();
const { brain, fetchBrain } = useBrain();

const uiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const isSaving = computed(() => uiFlags.value.updatingItem);

// models keeps every key from the draft so save() never drops a required one.
const models = reactive({});
const hasConflict = ref(false);
const isHydrated = ref(false);

// Per-agent API key — a backend connector, not part of the draft. We only ever
// hold the boolean status (the key is write-only) plus the new-key input.
const modelKeyConfigured = ref(false);
const apiKeyInput = ref('');
const isSavingKey = ref(false);
const isRemovingKey = ref(false);

const hydrateFromDraft = () => {
  const draft = brain.value.draft_config || {};
  Object.keys(models).forEach(key => delete models[key]);
  Object.assign(models, draft.models || {});
  modelKeyConfigured.value = !!brain.value.model_key_configured;
  isHydrated.value = true;
};

watch(
  () => brain.value.id,
  id => {
    if (id && !isHydrated.value) hydrateFromDraft();
  },
  { immediate: true }
);

const save = async () => {
  try {
    await store.dispatch('aiAgentBrains/update', {
      id: brain.value.id,
      draftConfig: {
        ...(brain.value.draft_config || {}),
        models: {
          ...(brain.value.draft_config?.models || {}),
          ...models,
        },
      },
      lockVersion: brain.value.lock_version,
    });
    hasConflict.value = false;
    useAlert(t('AI_AGENTS.MODEL.SAVED'));
  } catch (error) {
    if (error.response?.status === 409) {
      hasConflict.value = true;
    } else {
      useAlert(t('AI_AGENTS.MODEL.ERROR'));
    }
  }
};

const reloadDraft = async () => {
  await fetchBrain();
  hydrateFromDraft();
  hasConflict.value = false;
};

const saveModelKey = async () => {
  const apiKey = apiKeyInput.value.trim();
  if (!apiKey) return;
  isSavingKey.value = true;
  try {
    const { data } = await AiAgentBrainsAPI.setModelKey(brain.value.id, apiKey);
    modelKeyConfigured.value = !!data.model_key_configured;
    apiKeyInput.value = '';
    useAlert(t('AI_AGENTS.MODEL.API_KEY.SAVED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.MODEL.API_KEY.ERROR'));
  } finally {
    isSavingKey.value = false;
  }
};

const removeModelKey = async () => {
  isRemovingKey.value = true;
  try {
    const { data } = await AiAgentBrainsAPI.clearModelKey(brain.value.id);
    modelKeyConfigured.value = !!data.model_key_configured;
    useAlert(t('AI_AGENTS.MODEL.API_KEY.REMOVED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.MODEL.API_KEY.ERROR'));
  } finally {
    isRemovingKey.value = false;
  }
};
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4 max-w-3xl">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.HEADER') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.MODEL.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-if="hasConflict"
        class="flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{ t('AI_AGENTS.AGENT.CONFLICT.MESSAGE') }}
        <Button
          :label="t('AI_AGENTS.AGENT.CONFLICT.RELOAD')"
          color="amber"
          size="sm"
          @click="reloadDraft"
        />
      </div>

      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.MODEL.PROVIDER.LABEL') }}
        <select
          v-model="models.provider"
          class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option value="anthropic">
            {{ t('AI_AGENTS.MODEL.PROVIDER.OPTIONS.ANTHROPIC') }}
          </option>
        </select>
        <span class="text-xs font-normal text-n-slate-11">
          {{ t('AI_AGENTS.MODEL.PROVIDER.MORE_SOON') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.MODEL.MODEL_ID.LABEL') }}
        <input
          v-model="models.model"
          type="text"
          list="ai-agent-model-suggestions"
          :placeholder="t('AI_AGENTS.MODEL.MODEL_ID.PLACEHOLDER')"
          class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        />
        <datalist id="ai-agent-model-suggestions">
          <option
            v-for="suggestion in MODEL_SUGGESTIONS"
            :key="suggestion"
            :value="suggestion"
          />
        </datalist>
        <span class="text-xs font-normal text-n-slate-11">
          {{ t('AI_AGENTS.MODEL.MODEL_ID.HELP') }}
        </span>
      </label>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.PARAMS.MAX_TURNS') }}
          <input
            v-model.number="models.maxTurns"
            type="number"
            min="1"
            step="1"
            class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.PARAMS.MAX_BUDGET_USD') }}
          <input
            v-model.number="models.maxBudgetUsd"
            type="number"
            min="0"
            step="0.5"
            class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.PARAMS.DEBOUNCE_MS') }}
          <input
            v-model.number="models.debounceMs"
            type="number"
            min="0"
            step="100"
            class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.PARAMS.SESSION_TTL_SECONDS') }}
          <input
            v-model.number="models.sessionTtlSeconds"
            type="number"
            min="1"
            step="1"
            class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.PARAMS.PERMISSION_MODE') }}
          <input
            v-model="models.permissionMode"
            type="text"
            class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
        </label>
      </div>

      <div
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.MODEL.ADVANCED.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('AI_AGENTS.MODEL.ADVANCED.DESCRIPTION') }}
        </p>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.MODEL.ADVANCED.SDK_VERSION') }}
            <input
              :value="models.sdkVersion"
              type="text"
              readonly
              class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-alpha-1 text-n-slate-11"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.MODEL.ADVANCED.MCP_SERVER_NAME') }}
            <input
              :value="models.mcpServerName"
              type="text"
              readonly
              class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-alpha-1 text-n-slate-11"
            />
          </label>
        </div>
      </div>

      <div
        class="flex flex-col gap-3 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
      >
        <div class="flex items-center justify-between gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.MODEL.API_KEY.LABEL') }}
          </h3>
          <span
            v-if="modelKeyConfigured"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
          >
            {{ t('AI_AGENTS.MODEL.API_KEY.CONFIGURED') }}
          </span>
          <span
            v-else
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('AI_AGENTS.MODEL.API_KEY.NOT_CONFIGURED') }}
          </span>
        </div>

        <p class="text-xs text-n-slate-11">
          {{ t('AI_AGENTS.MODEL.API_KEY.HELP') }}
        </p>

        <div class="flex items-end gap-2">
          <label
            class="flex flex-col flex-1 gap-1 text-sm font-medium text-n-slate-12"
          >
            <input
              v-model="apiKeyInput"
              type="password"
              autocomplete="off"
              :placeholder="t('AI_AGENTS.MODEL.API_KEY.PLACEHOLDER')"
              class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
            />
          </label>
          <Button
            :label="t('AI_AGENTS.MODEL.API_KEY.SAVE')"
            :is-loading="isSavingKey"
            :disabled="!apiKeyInput.trim() || isSavingKey"
            size="sm"
            @click="saveModelKey"
          />
          <Button
            v-if="modelKeyConfigured"
            :label="t('AI_AGENTS.MODEL.API_KEY.REMOVE')"
            variant="outline"
            color="ruby"
            size="sm"
            :is-loading="isRemovingKey"
            :disabled="isRemovingKey"
            @click="removeModelKey"
          />
        </div>
      </div>

      <div
        class="flex flex-col gap-1 px-3 py-2 text-sm rounded-lg bg-n-alpha-1 text-n-slate-11"
      >
        {{ t('AI_AGENTS.MODEL.CREDENTIALS_NOTE') }}
      </div>

      <div class="flex justify-end">
        <Button
          :label="t('AI_AGENTS.MODEL.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
